import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import StatCard from '../components/StatCard'
import { dashboardAPI } from '../services/api'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import 'bootstrap-icons/font/bootstrap-icons.css'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip
)

// Images in CRA should live under `public/images` and be referenced via PUBLIC_URL.
// e.g. `frontend/public/images/logo.jpeg` -> `${PUBLIC_URL}/images/logo.jpeg`
const PUBLIC_URL = process.env.PUBLIC_URL || ''

const SLIDE_MS = 5000

// Each slide drifts toward a different point while it is on screen (Ken Burns)
const SLIDE_FOCUS = ['30% 40%', '75% 30%', '50% 75%', '25% 65%', '70% 55%']

const logos = [
  `${PUBLIC_URL}/images/image1.jpeg`,
  `${PUBLIC_URL}/images/image2.jpeg`,
  `${PUBLIC_URL}/images/image3.jpeg`,
  `${PUBLIC_URL}/images/image4.jpeg`,
  `${PUBLIC_URL}/images/image5.jpeg`,
]

const Home = () => {
  const { isAuthenticated, isSuperuser, user, userGroups, isFinanceManager } = useAuth()
  // Direct group-based and user-based access control
  const isAdmin = isSuperuser || userGroups.includes('Admin')
  const hasFullAccess = isSuperuser || isAdmin || isFinanceManager
  const navigate = useNavigate()
  const [stats, setStats] = useState({}) // Initialize as empty object instead of null
  const [loading, setLoading] = useState(true)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  useEffect(() => {
    // Only Staff, Manager, Finance Manager, Admin, or Superuser can see stats
    const allowedGroups = ['Staff', 'Manager', 'Finance Manager', 'Admin']
    const hasAccess = isSuperuser || userGroups.some(group => allowedGroups.includes(group))
    
    if (isAuthenticated && hasAccess) {
      // Add a small delay after login to allow cookies to propagate
      const loginTimestamp = localStorage.getItem('loginTimestamp')
      const recentLogin = loginTimestamp && (Date.now() - parseInt(loginTimestamp) < 10000) // 10 seconds
      
      if (recentLogin) {
        // Wait a bit before making API calls if we just logged in
        setTimeout(() => {
          fetchQuickStats()
        }, 2000)
      } else {
        fetchQuickStats()
      }
    } else {
      setLoading(false)
      // Clear stats if user doesn't have permission
      setStats({})
    }
  }, [isAuthenticated, isSuperuser, userGroups, hasFullAccess, isAdmin])

  // Auto-advance images every 5 seconds; picking one by hand restarts the timer
  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % logos.length)
    }, SLIDE_MS)
    return () => clearTimeout(timeout)
  }, [currentImageIndex])

  // Remember the slide that is fading out so it keeps its zoom while it fades
  const shownImageIndex = useRef(currentImageIndex)
  const previousImageIndex = useRef(null)
  if (shownImageIndex.current !== currentImageIndex) {
    previousImageIndex.current = shownImageIndex.current
    shownImageIndex.current = currentImageIndex
  }

  // Listen for refresh event from logo click
  useEffect(() => {
    const handleRefresh = () => {
      const allowedGroups = ['Staff', 'Manager', 'Finance Manager', 'Admin']
      const hasAccess = isSuperuser || userGroups.some(group => allowedGroups.includes(group))
      
      if (isAuthenticated && hasAccess) {
        fetchQuickStats()
      }
    }
    window.addEventListener('refreshHomePage', handleRefresh)
    return () => window.removeEventListener('refreshHomePage', handleRefresh)
  }, [isAuthenticated, isSuperuser, userGroups, hasFullAccess, isAdmin])

  const fetchQuickStats = async () => {
    try {
      const params = new URLSearchParams()
      params.append('skip_date_filter', 'true')
      const response = await dashboardAPI.getStats(params)
      if (response.data.status === 'success') {
        setStats(response.data.data)
      } else {
        // If API returns but status is not success, set empty stats
        setStats({})
      }
    } catch (error) {
      console.error('Error fetching stats:', error)
      // Set empty stats object so cards can still render with default values
      setStats({})
    } finally {
      setLoading(false)
    }
  }

  // Filter quickStats based on user type - matching Dashboard card styles exactly
  const allQuickStats = [
    {
      icon: 'bi-building',
      label: 'Unique Clients',
      value: stats?.surveys_by_client ? new Set(stats.surveys_by_client.map(c => c.client_name)).size : 0,
      cardClass: 'card-stat-clean card-stat-blue-special',
      link: '/dashboard',
      showFor: ['staff', 'clientuser'],
      order: 1,
    },
    {
      icon: 'bi-people',
      label: 'Active Surveyors',
      value: stats?.surveys_by_surveyor?.length || 0,
      cardClass: 'card-stat-clean card-stat-green-special',
      link: '/dashboard',
      showFor: ['staff'],
      order: 2,
    },
    {
      icon: 'bi-clipboard-data',
      label: 'Total Surveys',
      value: stats?.total_surveys || 0,
      cardClass: 'card-stat-clean card-stat-orange-special',
      link: '/dashboard',
      showFor: ['staff'],
      order: 3,
    },
    {
      icon: 'bi-globe',
      label: 'Countries Covered',
      value: stats?.surveys_by_country ? new Set(stats.surveys_by_country.map(c => c.survey_country)).size : 0,
      cardClass: 'card-stat-clean card-stat-purple-special',
      link: '/dashboard',
      showFor: ['staff'],
      order: 4,
    },
    {
      icon: 'bi-fuel-pump',
      label: 'Total Recovered Fuel (MT)',
      value: stats?.total_recovered_fuel_mt ? parseFloat(stats.total_recovered_fuel_mt).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0.000',
      cardClass: 'card-stat-clean card-stat-eco',
      link: '/dashboard',
      showFor: ['superuser', 'staff'],
      order: 5,
    },
    {
      icon: 'bi-cash-stack',
      label: 'Total Saving',
      value: stats?.total_saving ? `$${parseFloat(stats.total_saving).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '$0.00',
      cardClass: 'card-stat-clean card-stat-money',
      link: '/dashboard',
      showFor: ['superuser', 'staff'],
      order: 6,
    },
  ]
  
  // Filter stats based on user groups and sort by order
  const allowedGroups = ['Staff', 'Manager', 'Finance Manager', 'Admin']
  const hasAccess = isSuperuser || userGroups.some(group => allowedGroups.includes(group))
  
  const quickStats = allQuickStats
    .filter(stat => {
      if (hasFullAccess) {
        return stat.showFor.includes('superuser') || stat.showFor.includes('staff')
      } else if (hasAccess) {
        return stat.showFor.includes('staff')
      }
      return false
    })
    .sort((a, b) => a.order - b.order)

  // Feature cards - only show available ones
  const allFeatureCards = [
    {
      title: 'JNJ Operations',
      description: 'Manage operation calendar',
      icon: 'bi-calendar3',
      color: 'from-[#0029a4] via-[#016df7] to-[#0dbafd]',
      hoverColor: '',
      link: '/operations',
      available: true,
    },
    {
      title: 'JNJ Surveys',
      description: 'Manage survey records',
      icon: 'bi-list-check',
      color: 'from-[#0029a4] via-[#016df7] to-[#0dbafd]',
      hoverColor: '',
      link: '/surveys',
      available: hasAccess, // Only for Survey Staff, Manager, Admin
    },
    {
      title: 'JNJ Dashboard',
      description: 'View statistics and analytics',
      icon: 'bi-graph-up',
      color: 'from-[#0029a4] via-[#016df7] to-[#0dbafd]',
      hoverColor: '',
      link: '/dashboard',
      available: hasAccess, // Available for Survey Staff, Manager, Admin, and superusers
    },
  ]
  
  // Filter to only show available cards
  const featureCards = allFeatureCards.filter(card => card.available)

  // Prepare chart data for Total Surveys (last 12 months)
  const totalSurveysChartData = useMemo(() => {
    if (!stats?.surveys_by_month || !Array.isArray(stats.surveys_by_month) || stats.surveys_by_month.length === 0) {
      return null
    }

    // Get last 12 months of data
    const allMonths = [...stats.surveys_by_month]
    const last12Months = allMonths.slice(-12)

    // Ensure we have at least some data
    if (last12Months.length === 0) {
      return null
    }

    // Extract labels and data
    const labels = last12Months.map(item => {
      // Convert "Jan 2024" to "Jan" for shorter labels
      if (item.month && typeof item.month === 'string') {
        return item.month.split(' ')[0]
      }
      return ''
    }).filter(label => label !== '')
    
    const data = last12Months.map(item => item.count || 0)

    // Ensure we have valid data
    if (labels.length === 0 || data.length === 0) {
      return null
    }

    return {
      labels,
      datasets: [
        {
          label: 'Surveys',
          data,
          borderColor: 'rgba(255, 255, 255, 0.8)', // White with high opacity
          backgroundColor: 'rgba(255, 255, 255, 0.2)', // Light white fill
          borderWidth: 2.5,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
      ],
    }
  }, [stats?.surveys_by_month])

  // Prepare chart data for Fuel Recovery (last 12 months)
  const fuelRecoveryChartData = useMemo(() => {
    if (!stats?.surveys_by_month || !Array.isArray(stats.surveys_by_month) || stats.surveys_by_month.length === 0) {
      return null
    }

    // Get last 12 months of data
    const allMonths = [...stats.surveys_by_month]
    const last12Months = allMonths.slice(-12)

    // Ensure we have at least some data
    if (last12Months.length === 0) {
      return null
    }

    return {
      labels: last12Months.map(month => month.month || ''),
      datasets: [
        {
          data: last12Months.map(month => month.fuel_mt || 0),
          borderColor: 'rgba(255, 255, 255, 0.8)',
          backgroundColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 0,
        },
      ],
    }
  }, [stats?.surveys_by_month])

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        enabled: false,
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
      y: {
        display: false,
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
        beginAtZero: true,
      },
    },
    elements: {
      point: {
        radius: 0,
      },
      line: {
        borderWidth: 2,
      },
    },
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#dbe7ff] border-t-[#0a5fe0]"></div>
      </div>
    )
  }

  const displayName = user
    ? user.welcome_name ||
      (user.first_name || user.last_name
        ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
        : user.username)
    : ''

  return (
    <div className="min-h-screen bg-[#f4f7fc]">
      <style>{`
        @keyframes homeFadeUp {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .home-reveal { animation: homeFadeUp 0.5s ease-out both; }
        .home-slide {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          opacity: 0;
          transition: opacity 1.4s ease-in-out;
        }
        .home-slide.active { opacity: 1; }
        /* The outgoing slide keeps its zoom while it fades, so it never snaps back */
        .home-slide.active,
        .home-slide.leaving { animation: homeKenBurns 7s ease-out forwards; }
        @keyframes homeKenBurns {
          from { transform: scale(1); }
          to { transform: scale(1.14); }
        }
        .home-slide-progress { animation: homeSlideProgress linear forwards; }
        @keyframes homeSlideProgress {
          from { transform: scaleX(0); }
          to { transform: scaleX(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .home-reveal, .home-slide.active, .home-slide.leaving, .home-slide-progress { animation: none; }
          .home-slide { transition: none; }
        }
      `}</style>

      {/* Hero */}
      <section className="border-b border-[#d9e2f1] bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-10 px-4 pb-10 pt-6 sm:px-6 lg:grid-cols-2 lg:gap-14 lg:px-8 lg:pb-12 lg:pt-8">
          <div className="home-reveal order-2 lg:order-1">
            <p className="text-xs font-semibold uppercase tracking-[0.2em]">
              <span className="text-premium">Marine Survey Management</span>
            </p>
            <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-slate-900 sm:text-4xl xl:text-[44px]">
              Welcome to <span className="text-premium font-bold">J AND J</span>
              <br className="hidden sm:block" /> Marine Surveys
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Survey records, vessel operations and invoicing in one place, with{' '}
              <span className="font-semibold text-[#0a5fe0]">real-time analytics</span> and{' '}
              <span className="font-semibold text-[#0a5fe0]">comprehensive reporting</span>.
            </p>

            {isAuthenticated && user && (
              <p className="mt-6 border-t border-[#d9e2f1] pt-5 text-base text-slate-600">
                Welcome back, <span className="font-semibold text-slate-900">{displayName}</span>
              </p>
            )}

            {isAuthenticated && (
              <div className="mt-6 flex flex-wrap gap-3">
                {hasAccess && (
                  <button
                    type="button"
                    onClick={() => navigate('/surveys')}
                    className="bg-premium inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white shadow-[0_4px_14px_rgba(1,109,247,0.35)] transition-shadow hover:shadow-[0_6px_20px_rgba(1,109,247,0.45)]"
                  >
                    <i className="bi bi-list-check"></i>
                    Open Surveys
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => navigate('/operations')}
                  className="inline-flex items-center gap-2 rounded-lg border border-[#d9e2f1] bg-white px-5 py-2.5 text-sm font-semibold text-[#0a5fe0] transition-colors hover:border-[#0a5fe0] hover:bg-[#f4f7fc]"
                >
                  <i className="bi bi-calendar3"></i>
                  Operations Calendar
                </button>
              </div>
            )}
          </div>

          <div className="home-reveal order-1 lg:order-2" style={{ animationDelay: '80ms' }}>
            <div className="card-premium overflow-hidden p-1.5">
              <div className="relative aspect-[16/10] overflow-hidden rounded-xl bg-slate-100">
                {logos.map((logo, index) => (
                  <img
                    key={index}
                    src={logo}
                    alt={`JNJ Marine Survey ${index + 1}`}
                    className={`home-slide ${
                      index === currentImageIndex ? 'active' : index === previousImageIndex.current ? 'leaving' : ''
                    }`}
                    style={{ transformOrigin: SLIDE_FOCUS[index % SLIDE_FOCUS.length] }}
                  />
                ))}
                <div className="absolute inset-x-0 bottom-0 flex justify-center gap-2 bg-gradient-to-t from-black/35 to-transparent pb-4 pt-10">
                  {logos.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setCurrentImageIndex(index)}
                      className={`relative h-1.5 overflow-hidden rounded-full transition-all duration-300 ${
                        index === currentImageIndex ? 'w-10 bg-white/40' : 'w-1.5 bg-white/55 hover:bg-white/80'
                      }`}
                      aria-label={`Go to image ${index + 1}`}
                    >
                      {index === currentImageIndex && (
                        <span
                          key={currentImageIndex}
                          className="home-slide-progress absolute inset-0 origin-left rounded-full bg-white"
                          style={{ animationDuration: `${SLIDE_MS}ms` }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Key figures */}
        {isAuthenticated && hasAccess && quickStats.length > 0 && (
          <section className="mb-12">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
              {quickStats.map((stat, index) => (
                <StatCard
                  key={stat.label}
                  icon={stat.icon}
                  label={stat.label}
                  value={stat.value}
                  onClick={() => navigate(stat.link)}
                  className="home-reveal"
                  style={{ animationDelay: `${120 + index * 60}ms` }}
                />
              ))}
            </div>
          </section>
        )}

        {/* Quick access */}
        <section className="mb-12">
          <h2 className="flex items-center gap-2.5 text-xl font-semibold">
            <i className="bi bi-grid-3x3-gap icon-premium"></i>
            <span className="text-premium">Quick Access</span>
          </h2>
          <p className="mt-1 text-sm text-slate-500">Navigate to key features of the system</p>

          <div className={`mt-5 grid grid-cols-1 gap-5 ${featureCards.length >= 3 ? 'md:grid-cols-3' : featureCards.length === 2 ? 'md:grid-cols-2' : ''}`}>
            {featureCards.map((feature) => (
              <button
                type="button"
                key={feature.link}
                onClick={() => navigate(feature.link)}
                className="card-premium card-premium-interactive group flex items-center gap-4 p-5 text-left"
              >
                <span className="badge-metallic h-11 w-11 text-xl">
                  <i className={`bi ${feature.icon}`}></i>
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold text-slate-900">{feature.title}</span>
                  <span className="block text-sm text-slate-500">{feature.description}</span>
                </span>
                <i className="bi bi-arrow-right text-[#0a5fe0] transition-transform duration-200 group-hover:translate-x-1"></i>
              </button>
            ))}
          </div>
        </section>

        {/* About */}
        <section className="card-premium p-6 md:p-10">
          <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-2">
            <div>
              <h2 className="flex items-center gap-2.5 text-xl font-semibold">
                <i className="bi bi-info-circle icon-premium"></i>
                <span className="text-premium">J and J Surveys</span>
              </h2>
              <p className="mt-4 leading-relaxed text-slate-700">
                J and J Surveys provides a comprehensive platform for managing marine survey operations,
                tracking survey records, scheduling operations, and generating detailed reports and invoices.
              </p>
              <p className="mt-3 text-sm leading-relaxed text-slate-500">
                Our system enables efficient management of survey data, client information, surveyor assignments,
                and financial tracking, all in one integrated platform designed for the marine industry.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: 'bi-shield-check', title: 'Secure', text: 'Enterprise-grade security' },
                { icon: 'bi-speedometer2', title: 'Fast', text: 'Optimized performance' },
                { icon: 'bi-graph-up-arrow', title: 'Analytics', text: 'Real-time insights' },
                { icon: 'bi-people', title: 'Collaborative', text: 'Team-friendly' },
              ].map((item) => (
                <div key={item.title} className="rounded-xl border border-[#d9e2f1] bg-[#f8faff] p-5 text-center">
                  <span className="badge-metallic mx-auto h-10 w-10 text-lg">
                    <i className={`bi ${item.icon}`}></i>
                  </span>
                  <h4 className="mt-3 font-semibold text-slate-900">{item.title}</h4>
                  <p className="text-sm text-slate-500">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Call to Action */}
        {!isAuthenticated && (
          <section className="bg-premium mt-12 rounded-2xl p-8 text-center text-white md:p-12">
            <h2 className="text-2xl font-semibold">Get Started</h2>
            <p className="mt-2 text-white/85">Sign in to access the JNJ Marine Survey Management System</p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-white px-6 py-2.5 font-semibold text-[#0029a4] hover:bg-[#f4f7fc]"
            >
              <i className="bi bi-box-arrow-in-right"></i>
              Sign In
            </button>
          </section>
        )}
      </div>
    </div>
  )
}

export default Home
